import { useId, type ReactNode, Children, cloneElement, isValidElement } from 'react';
import { clsx } from 'clsx';

export interface FormFieldProps {
  /** Field label text */
  label: string;
  /** Error message displayed below the input */
  error?: string;
  /** Help text displayed below the input (hidden when error is shown) */
  helpText?: string;
  /** Show a red asterisk after the label */
  required?: boolean;
  /** Additional class names on the wrapper */
  className?: string;
  children: ReactNode;
}

export function FormField({ label, error, helpText, required, className, children }: FormFieldProps) {
  const id = useId();
  const inputId = `${id}-input`;
  const descriptionId = `${id}-desc`;
  const hasDescription = !!(error || helpText);

  // Inject id and aria-describedby into the child input element
  const enhanced = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    return cloneElement(child as React.ReactElement<Record<string, unknown>>, {
      id: (child.props as Record<string, unknown>).id ?? inputId,
      'aria-describedby': hasDescription ? descriptionId : undefined,
      error: error ?? (child.props as Record<string, unknown>).error,
    });
  });

  return (
    <div className={clsx('w-full', className)}>
      <label htmlFor={inputId} className="mb-1.5 block text-2xs font-medium text-ink-muted">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>

      {enhanced}

      {hasDescription && (
        <p id={descriptionId} className={clsx('mt-1 text-2xs', error ? 'text-danger' : 'text-ink-dim')}>
          {error || helpText}
        </p>
      )}
    </div>
  );
}
